/**
 * CrmCreateForm — the one create form for every CRM entity type.
 *
 * Renders inside `CreateSlideoutProvider`'s `SlideoutMenu` as the body of a
 * stack entry: header (title or nested breadcrumb), scrollable single-column
 * fields, footer actions. The header and footer render in every state —
 * loading, schema error, denied, ready — so the user always has a way out.
 *
 * Driven entirely by `CrmCreateConfig` (see ./types.ts). The generic list /
 * pipeline / picker callers build theirs with `buildGenericCreateConfig`;
 * bespoke flows (booking links) author sections with custom fields.
 *
 * What the form owns beyond rendering: schema-derived + config validation,
 * owner-association defaults, deal contact⇄company auto-fill, nested draft
 * creates, cardinality-conflict acknowledgement, "& add another" resets, and
 * the unsaved-changes registration the chrome's dismissal guard consults.
 */

import { AlertCircle, ChevronRight } from '@untitledui/icons';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Key } from 'react-aria-components';

import { useOrg } from '@/contexts/OrgContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { usePermissions } from '@/hooks/usePermissions';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { SurfaceBackButton } from '@/lib/base/overlays/surface-back-button';
import { useSurfaceDismiss } from '@/lib/base/overlays/surface-dismiss-context';
import { QueryErrorState } from '@/lib/components';
import { ConflictResolutionPanel } from '@/lib/crm/components/associations/ConflictResolutionPanel';
import { EntityBadgeById } from '@/lib/crm/components/badges';
import { buildFieldConfigFromSchema } from '@/lib/crm/core/buildFieldConfigFromSchema';
import type { DraftParentInfo } from '@/lib/crm/core/draftEntities';
import type { CardinalityConflict } from '@/lib/crm/core/types';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { useDraftEntities } from '@/lib/crm/hooks/useDraftEntities';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import {
  type PropertyTypeSchema,
  usePropertySchema,
} from '@/lib/crm/hooks/usePropertySchema';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { useStageTypeNames } from '@/lib/crm/hooks/useStageTypeNames';
import { ContactAlreadyExistsError } from '@/lib/crm/services';
import {
  buildEntityKindMap,
  counterpartEntityKinds,
} from '@/lib/crm/utils/associationMatching';
import { isCreatableEntityKind } from '@/lib/crm/utils/creatableKinds';
import { getDisplayNameFromProperties } from '@/lib/crm/utils/getDisplayName';
import { logger } from '@/lib/logger';
import { sanitizeError } from '@/lib/utils/errors';

import { buildCardinalityErrorMessage } from './cardinalityErrorMessage';
import {
  useCreateDismissGuard,
  useCreateSlideout,
  useIsTopCreateSlideout,
} from './CreateSlideoutContext';
import { createSlideoutInstance } from './createSlideoutInstance';
import {
  type AssociationRowContext,
  CrmCreateFields,
} from './CrmCreateFormFields';
import { CrmCreateFormFooter } from './CrmCreateFormFooter';
import { isFieldVisible } from './fieldVisibility';
import { buildSubmitPayload, isFieldEmpty } from './submitPayload';
import {
  type CrmCreateConfig,
  type CrmCreateField,
  isCrmCreateResult,
} from './types';
import { useAccordionErrorExpansion } from './useAccordionErrorExpansion';
import { useCreateConflicts } from './useCreateConflicts';
import { useDealAutoFill } from './useDealAutoFill';
import { useSubmitShortcut } from './useSubmitShortcut';

// Entity kinds whose associations are surfaced in the create form for custom
// (org_*) entity types. The create form is scoped to domain relationships; a
// counterpart whose kinds don't intersect this set is filtered out — most
// notably the kind-based "Reminders" association (its counterpart, ahoy_reminder,
// is event-kind, not in this set). Notes/tasks survive because ahoy_note is
// activity-kind. System (ahoy_*) entity types are left untouched, so their create
// forms still show every association (e.g. a contact's deal associations).
const CREATE_FORM_DOMAIN_KINDS = new Set([
  'asset',
  'party',
  'principal',
  'case',
  'activity',
]);

const CREATE_DEFAULT_STORAGE_KEY = 'crm-create-default';

const EMPTY_VALUES: Record<string, unknown> = {};

/** Breadcrumb: show at most this many titles, eliding older ones. */
const MAX_VISIBLE_BREADCRUMBS = 3;

function isOwnerAssociation(assoc: {
  target_entity_type_name: string;
  association_type_name: string;
}): boolean {
  return (
    assoc.target_entity_type_name === 'ahoy_user' &&
    assoc.association_type_name.endsWith('_to_owner')
  );
}

/**
 * Surface status for the form body — schema fetch, create-permission gate,
 * and the ready state, resolved once and switched exhaustively (REACT_19_PATTERNS
 * §1.2) instead of a sequence of independent boolean render branches.
 */
type CreateFormStatus =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'denied' }
  | { status: 'ready' };

function assertNeverFormStatus(x: never): never {
  throw new Error(`Unhandled create form status: ${JSON.stringify(x)}`);
}

function allFields(config: CrmCreateConfig): CrmCreateField[] {
  return config.sections.flatMap(section => section.fields);
}

// ─── Header ──────────────────────────────────────────────────────────────────

function CreateFormTitle({
  title,
  titles,
}: {
  title: string;
  titles: string[];
}) {
  if (titles.length <= 1) {
    return title;
  }
  const hasOverflow = titles.length > MAX_VISIBLE_BREADCRUMBS;
  const visible = hasOverflow ? titles.slice(-MAX_VISIBLE_BREADCRUMBS) : titles;
  return (
    <>
      {hasOverflow && (
        <span className='flex items-center gap-1 flex-shrink-0'>
          <span className='text-tertiary font-normal'>…</span>
          <ChevronRight className='size-4 text-quaternary' />
        </span>
      )}
      {visible.map((t, i) => {
        const isLast = i === visible.length - 1;
        return (
          <span
            // eslint-disable-next-line react/no-array-index-key -- stable append-only stack order
            key={i}
            className={
              isLast
                ? 'flex items-center gap-1 flex-shrink-0'
                : 'flex items-center gap-1 min-w-0'
            }
          >
            <span
              className={
                isLast
                  ? 'whitespace-nowrap'
                  : 'text-tertiary font-normal truncate'
              }
            >
              {t}
            </span>
            {!isLast && (
              <ChevronRight className='size-4 text-quaternary flex-shrink-0' />
            )}
          </span>
        );
      })}
    </>
  );
}

// ─── Content ─────────────────────────────────────────────────────────────────

interface CrmCreateFormContentProps {
  config: CrmCreateConfig;
  formData: Record<string, unknown>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  propertySchemas: Record<string, PropertyTypeSchema>;
  schemasLoading: boolean;
  schemasError: Error | null;
}

function CrmCreateFormContent({
  config,
  formData,
  setFormData,
  propertySchemas,
  schemasLoading,
  schemasError,
}: CrmCreateFormContentProps) {
  const {
    orgId,
    entityType,
    title,
    submitLabel = 'Create',
    initialValues = EMPTY_VALUES,
    showCreateAnother = true,
    onPropertyChange,
    draftParentContext,
    onSubmit,
    onCreated,
    sectionStyle = 'flat',
    sections,
    renderFooterSecondaryAction,
  } = config;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ReactNode | null>(null);
  const {
    getFieldProps,
    resetTouched,
    setSubmitError,
    touchAll,
    visibleErrors,
  } = useFormContext();

  // Narrow screens collapse the submit cluster into a split button whose
  // default action the user picks once.
  const isDesktop = useBreakpoint('sm');
  const [preferCreateAnother, setPreferCreateAnother] = useState(() => {
    try {
      return localStorage.getItem(CREATE_DEFAULT_STORAGE_KEY) === 'another';
    } catch {
      return false;
    }
  });
  const toggleCreateDefault = useCallback((key: Key) => {
    const another = key === 'another';
    setPreferCreateAnother(another);
    try {
      localStorage.setItem(
        CREATE_DEFAULT_STORAGE_KEY,
        another ? 'another' : 'create'
      );
    } catch {
      // Ignore localStorage errors (private browsing, quota, etc.)
    }
  }, []);

  // Slideout stack chrome: programmatic close, nested breadcrumb, shortcut gate.
  const { pop, requestClose, titles: stackTitles } = useCreateSlideout();
  const isTop = useIsTopCreateSlideout();
  const surface = useSurfaceDismiss();
  // The chrome's own close is already guarded; outside any chrome (tests,
  // stories) fall back to the stack's guarded close.
  const handleClose = surface?.close ?? requestClose;

  // Whether the user has edited a field. Deliberately NOT a diff of formData
  // against its initial value: formData is written by several post-mount
  // effects (schema defaults, association backfill, owner auto-fill, deal
  // auto-fill, hidden-field resets), so a value diff reports dirty on a form
  // nobody has touched. Only the user-edit entry points below flip this.
  const [hasUserEdits, setHasUserEdits] = useState(false);
  useCreateDismissGuard(hasUserEdits);

  // Track owner associations the user has manually cleared so the
  // catch-up effect does not re-fill them.
  const manuallyClearedOwnersRef = useRef<Set<string>>(new Set());

  // Get current user's entity_id for defaulting owner associations
  const { entityId: currentUserEntityId } = useCurrentUserEntity();

  // Get org context for default currency
  const { currentOrg } = useOrg();

  // Fetch association type schemas
  const { associationTypes, isLoading: associationTypesLoading } =
    useAssociationTypes(orgId);

  // Build set of stage entity type names for generic pipeline stage detection.
  // Uses shared 'object-types-all' cache — no additional API call.
  const { stageTypeNames, isLoading: stageTypesLoading } =
    useStageTypeNames(orgId);

  // Entity-kind map (entity_type_name → kinds[]) for the custom-type association
  // filter below. Shares the warm 'object-types-all' cache — no extra request.
  const { data: objectTypesData } = useObjectTypes(orgId);
  const entityKindMap = useMemo(
    () => buildEntityKindMap(objectTypesData?.allObjectTypes ?? []),
    [objectTypesData?.allObjectTypes]
  );

  // Field inventory by kind. Names drive schema lookups, the submit payload
  // split and validation; the sections themselves drive rendering.
  const fields = useMemo(() => allFields(config), [config]);
  const propertyNames = useMemo(
    () =>
      new Set(fields.flatMap(f => (f.kind === 'property' ? [f.property] : []))),
    [fields]
  );
  const customNames = useMemo(
    () =>
      new Set(fields.flatMap(f => (f.kind === 'custom' ? [f.property] : []))),
    [fields]
  );
  const createAssociations = useMemo(
    () =>
      fields.flatMap(f =>
        f.kind === 'association' ? [f.associationType] : []
      ),
    [fields]
  );

  // Parent identity handed down to nested (depth-1) association creates, so
  // their own draft-parent banner can reference this form's in-progress name.
  const parentDisplayName =
    getDisplayNameFromProperties(formData, null) ?? undefined;
  const draftParentInfo = useMemo<DraftParentInfo>(
    () => ({ entityTypeName: entityType, displayName: parentDisplayName }),
    [entityType, parentDisplayName]
  );

  // Three independent gates, all of which must pass before the form renders.
  // They answer different questions and none subsumes another — the same trio
  // AssociatedCrmObjectInput checks before offering its "Create new" row.
  //
  //  1. Does the ONTOLOGY allow generic creation of this kind? (party/case/asset)
  //     Only for the type-agnostic path — a bespoke config is itself the
  //     decision that this type is creatable here, so it does not opt in
  //     (see `enforceGenericCreatableKinds`).
  //  2. Does the SCHEMA allow any user to create this type at all? A type whose
  //     ahoy_object_created resolves to internal-only (user_creatable false) is
  //     system-managed — e.g. ahoy_web_visitor, written only by web tracking —
  //     and the backend rejects a user-sourced create whatever permissions the
  //     caller holds. A missing field (cached pre-rollout schema) is treated as
  //     creatable; the backend still enforces.
  //  3. Does THIS USER hold create permission on it?
  //
  // Fail-open on an empty kind map keeps existing behaviour during load and in
  // tests that don't populate object types.
  const { hasPermission } = usePermissions();
  const isCreatableKind =
    !config.enforceGenericCreatableKinds ||
    isCreatableEntityKind(entityType, entityKindMap);
  const typeUserCreatable =
    objectTypesData?.allObjectTypes?.find(
      t => t.entity_type_name === entityType
    )?.user_creatable !== false;
  const isCreatable =
    isCreatableKind &&
    typeUserCreatable &&
    hasPermission(entityType, 'ahoy_object_created');

  // Slideout stack for opening CRM object editors from error messages
  const { pushSlideout } = useSlideoutStack();

  // Filter association types to only those requested, preserving order
  const filteredAssociationTypes = useMemo(() => {
    const selected = createAssociations
      .map(name =>
        associationTypes.find(assoc => assoc.association_type_name === name)
      )
      .filter((assoc): assoc is NonNullable<typeof assoc> => assoc != null);

    // For custom (org_*) entity types, restrict the create form to domain
    // associations: keep an association only if its counterpart entity type's
    // kinds intersect CREATE_FORM_DOMAIN_KINDS. This hides system/utility
    // associations (e.g. the kind-based "Reminders" link, whose counterpart
    // ahoy_reminder is event-kind) while keeping company/contact/user/notes/
    // workspace links. System entity types are left untouched. Fail open while
    // the kind/stage caches are still warming so fields never flash hidden.
    if (
      !entityType.startsWith('org_') ||
      entityKindMap.size === 0 ||
      stageTypesLoading
    ) {
      return selected;
    }
    return selected.filter(assoc => {
      // Always keep the pipeline-stage association. It is core pipeline
      // infrastructure — the create form's stage select — not a "domain"
      // association subject to the allowlist below ('stage' is deliberately not
      // a CREATE_FORM_DOMAIN_KIND). GenericCrmList adds this association to
      // createAssociations for pipelined custom (org_*) entities; without this
      // exemption the domain-kind filter strips it back out and the create
      // form loses its stage select.
      if (stageTypeNames.has(assoc.target_entity_type_name)) {
        return true;
      }
      return counterpartEntityKinds(assoc, entityType, entityKindMap).some(
        kind => CREATE_FORM_DOMAIN_KINDS.has(kind)
      );
    });
  }, [
    associationTypes,
    createAssociations,
    entityType,
    entityKindMap,
    stageTypeNames,
    stageTypesLoading,
  ]);
  const associationTypesByName = useMemo(
    () =>
      new Map(
        filteredAssociationTypes.map(assoc => [
          assoc.association_type_name,
          assoc,
        ])
      ),
    [filteredAssociationTypes]
  );
  const associationTypeNames = useMemo(
    () => new Set(associationTypesByName.keys()),
    [associationTypesByName]
  );

  // Draft map for nested-create flows (associations pointing at a draft not
  // yet persisted server-side).
  const { drafts, addDraft, clearDrafts, collectReferencedDrafts } =
    useDraftEntities();
  const draftIds = useMemo(() => new Set(Object.keys(drafts)), [drafts]);
  // Latest-state draft check for async callers (the debounced deal auto-fill):
  // `collectReferencedDrafts` reads the hook's current map, so a draft that
  // landed after the caller's closure was created is still recognised.
  const isDraftId = useCallback(
    (id: string) =>
      collectReferencedDrafts([{ target_entity_id: id }]).length > 0,
    [collectReferencedDrafts]
  );

  const { handleDealAutoFill, pendingInitialAutoFillRef } = useDealAutoFill({
    entityType,
    orgId,
    setFormData,
    draftParentContext,
    isDraftId,
    formData,
  });

  const {
    conflicts,
    conflictsError,
    ackedEntityIds,
    blockedByConflicts,
    conflictPanelRef,
    handleAcknowledgeConflict,
    handleRemoveConflictTarget,
    resetAcked,
  } = useCreateConflicts({
    orgId,
    entityType,
    formData,
    filteredAssociationTypes,
    draftIds,
    setFormData,
  });

  // Bumped on every "& add another" reset. Included in each association
  // field's React key so the pickers remount on reset — this runs each picker's
  // unmount cleanup, which evicts any never-submitted seq-0 draft summaries it
  // pushed into the store and clears its stale internal draft-tracking. Without
  // it, `clearDrafts()` empties the submission map but a picker's own seq-0
  // summary lingers in the mounted dropdown, so the next entry can show and
  // re-select that phantom draft id — which then submits with no `draft_entities`
  // and is rejected. (A just-submitted draft is already at seq >= 1, so the
  // seq-0-guarded eviction correctly leaves the real record in place.)
  const [formResetKey, setFormResetKey] = useState(0);

  // The values the form was initialised with, for hidden-field resets below.
  const initialDataRef = useRef<Record<string, unknown>>(EMPTY_VALUES);

  // Initialize form data on mount (the component mounts fresh per open)
  useEffect(() => {
    const initialData: Record<string, unknown> = {};

    // Property values default from the schema; custom values only from the
    // caller's initialValues.
    for (const field of fields) {
      if (field.kind === 'property') {
        const schema = propertySchemas[field.property];
        const defaultValue = schema?.default_value ?? '';
        initialData[field.property] =
          initialValues[field.property] ?? defaultValue;
      } else if (field.kind === 'custom') {
        initialData[field.property] = initialValues[field.property];
      }
    }

    // Initialize associations - default system owner associations to current user
    // Only auto-fill known system associations ending in '_to_owner', not arbitrary
    // custom user associations (e.g., custom "Project Manager" field).
    filteredAssociationTypes.forEach(assoc => {
      const providedValue = initialValues[assoc.association_type_name];
      if (providedValue !== undefined) {
        initialData[assoc.association_type_name] = providedValue;
      } else if (isOwnerAssociation(assoc) && currentUserEntityId) {
        initialData[assoc.association_type_name] = [currentUserEntityId];
      } else {
        initialData[assoc.association_type_name] = null;
      }
    });

    initialDataRef.current = initialData;
    setFormData(initialData);
    setError(null);

    // Store initial data for auto-fill processing after this effect completes
    if (entityType === 'ahoy_deal') {
      pendingInitialAutoFillRef.current = initialData;
    }

    return () => {
      pendingInitialAutoFillRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Mount only — the stack mounts a fresh form per open

  // Backfill association keys that were missing at mount time.
  // The mount-only init effect above captures an empty filteredAssociationTypes
  // when association types haven't loaded yet, so formData starts with no
  // association keys. This effect fills them in once association types arrive,
  // which unblocks the auto-select effect in AssociatedPipelineStageInput
  // (it needs the key to exist as null so its onChange updater composes correctly
  // with formData rather than racing against a stale closure).
  useEffect(() => {
    if (filteredAssociationTypes.length === 0) {
      return;
    }

    setFormData(prev => {
      let changed = false;
      const updated = { ...prev };

      filteredAssociationTypes.forEach(assoc => {
        if (!(assoc.association_type_name in updated)) {
          const providedValue = initialValues[assoc.association_type_name];
          updated[assoc.association_type_name] =
            providedValue !== undefined ? providedValue : null;
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [filteredAssociationTypes, initialValues, setFormData]);

  // Set user default when currentUserEntityId becomes available after mount
  // Only auto-fill system owner associations, not custom user associations
  useEffect(() => {
    if (!currentUserEntityId) {
      return;
    }
    setFormData(prev => {
      const updated = { ...prev };
      let changed = false;

      filteredAssociationTypes.forEach(assoc => {
        const hasNoInitialValue =
          initialValues[assoc.association_type_name] === undefined;
        const currentValue = prev[assoc.association_type_name];
        // Field is unfilled if it was never initialized (undefined, e.g. cold cache)
        // or was initialized as empty (null) by the mount effect
        const isUnfilled = currentValue === undefined || currentValue === null;

        const wasManuallyCleared = manuallyClearedOwnersRef.current.has(
          assoc.association_type_name
        );
        if (
          isOwnerAssociation(assoc) &&
          hasNoInitialValue &&
          isUnfilled &&
          !wasManuallyCleared
        ) {
          updated[assoc.association_type_name] = [currentUserEntityId];
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [
    currentUserEntityId,
    filteredAssociationTypes,
    initialValues,
    setFormData,
  ]);

  // Reset conditional fields that just became hidden to their initial value,
  // so the submitted payload matches what the user could see and the field
  // keeps its type (an array-backed field stays an array). A property may
  // back multiple conditional fields with different labels (e.g.
  // ahoy_custom_location renders as "Zoom Meeting URL" or "Custom location"
  // depending on ahoy_location_type) — only reset a property when every
  // field bound to it is hidden, otherwise the hidden twin wipes what the
  // user types into the visible one on every keystroke. Never marks the form
  // dirty: nobody typed anything.
  //
  // Functional updater, not a replace: on mount this runs in the same effect
  // flush as the schema-default / association-backfill / owner-default
  // effects, all of which queue functional updates. A replace computed from
  // this render's `formData` would win that flush with a stale snapshot and
  // discard what they just queued.
  useEffect(() => {
    setFormData(prev => {
      const visibleNames = new Set<string>();
      const conditionalNames = new Set<string>();
      for (const field of fields) {
        if (field.kind === 'association') {
          continue;
        }
        if (field.condition) {
          conditionalNames.add(field.property);
        }
        if (isFieldVisible(field, prev, associationTypesByName)) {
          visibleNames.add(field.property);
        }
      }
      const initial = initialDataRef.current;
      let changed = false;
      const updated = { ...prev };
      for (const name of conditionalNames) {
        if (visibleNames.has(name)) {
          continue;
        }
        // JSON equality prevents the effect from looping when current
        // already equals initial by value.
        if (JSON.stringify(prev[name]) !== JSON.stringify(initial[name])) {
          updated[name] = initial[name];
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [fields, formData, associationTypesByName, setFormData]);

  // Accordion expand state, plus auto-expand-to-error for sectionStyle
  // 'accordion' (only meaningful there).
  const { expandedKeys, handleExpandedChange } = useAccordionErrorExpansion({
    sections,
    sectionStyle,
    visibleErrors,
  });

  const resetForAnother = useCallback(() => {
    const resetData: Record<string, unknown> = {};
    for (const field of fields) {
      if (field.kind === 'property') {
        resetData[field.property] =
          propertySchemas[field.property]?.default_value ?? '';
      } else if (field.kind === 'custom') {
        resetData[field.property] = initialValues[field.property];
      }
    }
    filteredAssociationTypes.forEach(assoc => {
      resetData[assoc.association_type_name] = null;
    });
    setFormData(resetData);
    resetTouched();
    // The entry the user filled in has been created; what is on screen now
    // is a blank form nobody has touched.
    setHasUserEdits(false);
    resetAcked();
    clearDrafts();
    // Remount the association pickers so each evicts its never-submitted
    // seq-0 draft summaries (see formResetKey declaration) — clearDrafts
    // alone leaves those summaries selectable in the still-mounted picker.
    setFormResetKey(k => k + 1);
  }, [
    fields,
    propertySchemas,
    initialValues,
    filteredAssociationTypes,
    setFormData,
    resetTouched,
    resetAcked,
    clearDrafts,
  ]);

  const handleSubmit = useCallback(
    async (shouldCreateAnother = false) => {
      if (!isCreatable) {
        setError("This type of record can't be created here.");
        return;
      }

      // Guard: move focus to conflict panel if conflicts are unresolved.
      if (blockedByConflicts) {
        setError(
          'Resolve the highlighted conflicts before creating this record.'
        );
        // react-aria's Checkbox renders a native `<input type="checkbox">`
        // with an implicit (not explicit `role="checkbox"`) ARIA role, so the
        // selector targets the input element directly.
        const firstCheckbox =
          conflictPanelRef.current?.querySelector<HTMLElement>(
            'input[type="checkbox"]'
          );
        // Defer focus to the next frame so the browser has a chance to paint
        // the newly-set error banner first — gives the role="alert" region
        // priority to be announced before focus moves to the checkbox.
        requestAnimationFrame(() => firstCheckbox?.focus());
        return;
      }

      setError(null);
      setIsSubmitting(true);
      try {
        const payload = buildSubmitPayload({
          formData,
          propertyNames,
          customNames,
          associationTypeNames,
          defaultCurrency: currentOrg?.defaultCurrency,
          collectReferencedDrafts,
          ackedConflictKeys: ackedEntityIds,
        });

        const result = await onSubmit(payload);

        if (onCreated && isCrmCreateResult(result)) {
          onCreated(result.entity_id);
        }

        if (shouldCreateAnother) {
          resetForAnother();
        } else {
          pop(result);
        }
      } catch (err) {
        // Handle existing contact with lifecycle stage — show badge instead of error
        if (err instanceof ContactAlreadyExistsError) {
          const contact = err.existingContact;
          setError(
            <div className='flex flex-col gap-2'>
              <p>This contact is already in your CRM.</p>
              <EntityBadgeById
                entityId={contact.entity_id}
                orgId={contact.org_id}
              />
            </div>
          );
          return;
        }

        logger.error(
          'Error submitting CRM form',
          err instanceof Error ? err : undefined,
          { component: 'crm_events' }
        );

        // Parse validation errors from API response
        let errorContent: ReactNode = "We couldn't submit this form";

        if (err instanceof Error) {
          const errorData = (
            err as {
              response?: {
                error?: string;
                error_type?: string;
                detail?: {
                  association_type_name?: string;
                  direction?: string;
                  conflicts?: CardinalityConflict[];
                };
                validation_errors?: Array<{
                  event_index: number;
                  entity_type: string;
                  event_type: string;
                  message: string;
                  idempotency_key?: string;
                }>;
              };
            }
          ).response;

          if (
            errorData?.error_type === 'association_cardinality_violation' &&
            errorData.detail
          ) {
            errorContent = buildCardinalityErrorMessage(
              errorData.detail,
              associationTypes,
              pushSlideout
            );
          } else if (
            errorData?.validation_errors &&
            Array.isArray(errorData.validation_errors)
          ) {
            const topMessage = errorData.error || 'Validation failed';
            const messages = errorData.validation_errors.map(ve => ve.message);
            const uniqueMessages = messages.filter(m => m !== topMessage);
            if (uniqueMessages.length > 0) {
              errorContent = `${topMessage}\n\n${uniqueMessages.map(m => `• ${m}`).join('\n')}`;
            } else {
              errorContent = topMessage;
            }
          } else if (errorData?.error) {
            errorContent = sanitizeError(errorData.error);
          } else {
            errorContent = sanitizeError(err);
          }
        }

        // Show in both the inline banner and the submit button tooltip
        setError(errorContent);
        setSubmitError(
          typeof errorContent === 'string'
            ? errorContent
            : 'Submission failed – see error above'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isCreatable,
      blockedByConflicts,
      formData,
      propertyNames,
      customNames,
      associationTypeNames,
      currentOrg?.defaultCurrency,
      collectReferencedDrafts,
      ackedEntityIds,
      conflictPanelRef,
      onSubmit,
      onCreated,
      resetForAnother,
      pop,
      associationTypes,
      pushSlideout,
      setSubmitError,
    ]
  );

  // Handle CMD+Enter to submit with "create another" (only when that option
  // is available and this form is the visible top of the stack).
  useSubmitShortcut({
    handleSubmit,
    isTop,
    isSubmitting,
    showCreateAnother,
  });

  const handleSubmitClose = useCallback(
    () => handleSubmit(false),
    [handleSubmit]
  );
  const handleSubmitAnother = useCallback(
    () => handleSubmit(true),
    [handleSubmit]
  );
  const handleSubmitPreferred = useCallback(
    () => handleSubmit(preferCreateAnother),
    [handleSubmit, preferCreateAnother]
  );

  const handlePropertyChange = useCallback(
    (propertyTypeName: string, value: unknown) => {
      setHasUserEdits(true);
      setFormData(prev => ({ ...prev, [propertyTypeName]: value }));
      onPropertyChange?.(propertyTypeName, value);
    },
    [setFormData, onPropertyChange]
  );

  const handleCustomChange = useCallback(
    (name: string, value: unknown) => {
      setHasUserEdits(true);
      setFormData(prev => ({ ...prev, [name]: value }));
    },
    [setFormData]
  );

  /**
   * True when an association change is `AssociatedPipelineStageInput` filling
   * an empty stage field with the first stage of the default pipeline.
   *
   * That input defaults itself through the same `onChange` a user pick comes
   * through, so without this the guard would treat every stage-bearing create
   * form as dirty the moment its stages finish loading — nobody having touched
   * it — and teach people to click past the confirmation. The default only
   * ever fires on an empty field; a real user pick replaces a value the
   * default already put there, so it still counts.
   */
  const isStageSelfDefaulting = useCallback(
    (associationTypeName: string) => {
      if (!isFieldEmpty(formData[associationTypeName])) {
        return false;
      }
      return filteredAssociationTypes.some(
        assoc =>
          assoc.association_type_name === associationTypeName &&
          stageTypeNames.has(assoc.target_entity_type_name)
      );
    },
    [formData, filteredAssociationTypes, stageTypeNames]
  );

  // An Effect Event: its own identity never changes across renders, so the
  // per-association-type callbacks cached below can be created once each and
  // still always run this logic's LATEST closure (isStageSelfDefaulting,
  // handleDealAutoFill, etc.) — caching them introduces no staleness. Not a
  // ref write during render: useEffectEvent is the sanctioned latest-value
  // mechanism (REACT_19_PATTERNS.md § 4.1).
  const handleAssociationChangeEvent = useEffectEvent(
    (associationTypeName: string, value: string | string[] | null) => {
      // Track manual clears of owner associations so the catch-up effect
      // does not re-fill them after the user explicitly removed the value.
      if (value === null && associationTypeName.endsWith('_to_owner')) {
        manuallyClearedOwnersRef.current.add(associationTypeName);
      } else if (value !== null && associationTypeName.endsWith('_to_owner')) {
        manuallyClearedOwnersRef.current.delete(associationTypeName);
      }
      if (!isStageSelfDefaulting(associationTypeName)) {
        setHasUserEdits(true);
      }
      setFormData(prev => ({ ...prev, [associationTypeName]: value }));

      setFormData(prev => {
        const currentFormData = { ...prev, [associationTypeName]: value };
        void handleDealAutoFill(associationTypeName, value, currentFormData);
        return prev;
      });
    }
  );

  // Cache of per-association-type onChange callbacks, keyed by
  // association_type_name, populated lazily during render. A name gets one
  // callback for as long as it stays in filteredAssociationTypes, so
  // filteredAssociationTypes changing reference (without its relevant
  // contents changing) never invalidates callbacks an association row
  // already holds — each cached callback delegates to
  // handleAssociationChangeEvent, which is always current.
  const associationCallbacksCacheRef = useRef<
    Map<string, (value: string | string[] | null) => void>
  >(new Map());

  const associationCallbacks = useMemo(() => {
    const cache = associationCallbacksCacheRef.current;
    const activeNames = new Set(
      filteredAssociationTypes.map(assoc => assoc.association_type_name)
    );
    // Drop entries for types no longer rendered so the cache doesn't grow
    // unboundedly as the config/entity type changes.
    for (const name of cache.keys()) {
      if (!activeNames.has(name)) {
        cache.delete(name);
      }
    }
    const callbacks: Record<string, (value: string | string[] | null) => void> =
      {};
    filteredAssociationTypes.forEach(assoc => {
      const name = assoc.association_type_name;
      let callback = cache.get(name);
      if (!callback) {
        callback = value => handleAssociationChangeEvent(name, value);
        cache.set(name, callback);
      }
      callbacks[name] = callback;
    });
    return callbacks;
    // handleAssociationChangeEvent is an Effect Event: always current
    // without being a reactive dependency (REACT_19_PATTERNS.md § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAssociationTypes]);

  // Button.tsx renders a disabled+tooltipped SubmitButton as a non-interactive
  // TooltipTrigger with no onClick forwarded (see button.tsx's disabled path),
  // so SubmitButton's own touchAll()-then-submit click handler never runs while
  // the form is invalid. The underlying element is still a real <button> (no
  // native `disabled` attribute — it's only `aria-disabled`), so it still
  // dispatches a DOM click that bubbles up to the footer's wrapper. That
  // capture-phase handler is the only way to observe the click and mark all
  // fields touched, which lets the visibleErrors-driven accordion auto-expand
  // above find and reveal the hidden error.
  const handleSubmitClusterClickCapture = useCallback(() => {
    touchAll();
  }, [touchAll]);

  const parentTypeLabel =
    objectTypesData?.allObjectTypes
      ?.find(t => t.entity_type_name === draftParentContext?.entityTypeName)
      ?.label?.toLowerCase() ?? 'record';

  const associationCtx = useMemo<AssociationRowContext>(
    () => ({
      orgId,
      disabled: isSubmitting,
      stageTypeNames,
      formResetKey,
      draftParentContext,
      parentTypeLabel,
      draftParentInfo,
      onCreateDraft: addDraft,
      associationCallbacks,
    }),
    [
      orgId,
      isSubmitting,
      stageTypeNames,
      formResetKey,
      draftParentContext,
      parentTypeLabel,
      draftParentInfo,
      addDraft,
      associationCallbacks,
    ]
  );

  const isReady =
    !schemasLoading && !stageTypesLoading && !associationTypesLoading;

  // Resolved once; the switch below dispatches on it exhaustively instead of
  // branching on schemasLoading / schemasError / isCreatable independently.
  // The schema-error state defers to a pending submit error (matching the
  // banner rendered above it) so the two don't both compete for the slot.
  let formStatus: CreateFormStatus;
  if (schemasLoading) {
    formStatus = { status: 'loading' };
  } else if (schemasError && !error) {
    formStatus = { status: 'error', error: schemasError };
  } else if (!isCreatable) {
    formStatus = { status: 'denied' };
  } else {
    formStatus = { status: 'ready' };
  }

  return (
    <div
      className='flex size-full min-h-0 flex-1 flex-col'
      data-testid='crm-create-form'
      data-component='create-form'
      data-instance={createSlideoutInstance(entityType)}
    >
      {/* E2E readiness signal: the slideout's own ready marker flips when
          its enter transition ends, which can precede the schema fetch. This
          one reflects "form is interactable", so e2e waits on it before
          typing. */}
      <span
        aria-hidden
        data-component='create-form-ready-marker'
        data-ready={!schemasLoading ? '' : undefined}
      />
      <SlideoutMenu.Header onClose={handleClose}>
        <div className='flex min-w-0 items-center gap-1.5'>
          {/* Renders only when this entry has one beneath it. */}
          <SurfaceBackButton />
          <div className='flex min-w-0 flex-col gap-1'>
            {config.eyebrow && (
              <div className='flex items-center gap-1.5 text-body-small font-medium text-[var(--color-text-secondary)]'>
                {config.eyebrow}
              </div>
            )}
            <h2 className='text-heading-4 text-[var(--color-text-primary)] flex items-center gap-1 overflow-hidden'>
              <CreateFormTitle title={title} titles={stackTitles} />
            </h2>
          </div>
        </div>
      </SlideoutMenu.Header>
      <SlideoutMenu.Content>
        <div className='flex flex-col gap-6 pb-4'>
          {error && (
            <div
              role='alert'
              className='p-4 bg-utility-error-50 border border-utility-error-200 rounded-lg'
            >
              <div className='flex items-start gap-3'>
                <AlertCircle className='w-5 h-5 text-utility-error-600 flex-shrink-0 mt-0.5' />
                <div className='flex-1'>
                  <p className='text-sm font-medium text-utility-error-800'>
                    Error
                  </p>
                  <div className='text-sm text-utility-error-700 mt-1 whitespace-pre-wrap'>
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}
          {(() => {
            switch (formStatus.status) {
              case 'loading':
                return (
                  <div className='text-center py-8'>
                    <p className='text-sm text-tertiary'>Loading form…</p>
                  </div>
                );
              case 'error':
                return (
                  <QueryErrorState
                    error={formStatus.error}
                    label="We couldn't load property schemas"
                    size='sm'
                  />
                );
              case 'denied':
                return (
                  <div className='text-center py-8'>
                    <p className='text-sm text-tertiary'>
                      This type of record can't be created here.
                    </p>
                  </div>
                );
              case 'ready':
                return (
                  <CrmCreateFields
                    sections={sections}
                    sectionStyle={sectionStyle}
                    formData={formData}
                    propertySchemas={propertySchemas}
                    associationTypesByName={associationTypesByName}
                    getFieldProps={getFieldProps}
                    onPropertyChange={handlePropertyChange}
                    onCustomChange={handleCustomChange}
                    disabled={isSubmitting}
                    associationCtx={associationCtx}
                    expandedKeys={expandedKeys}
                    onExpandedChange={handleExpandedChange}
                  />
                );
              default:
                return assertNeverFormStatus(formStatus);
            }
          })()}

          {/* Conflict resolution panel — shown only once a real conflict or
              validation error surfaces. The in-flight check is deliberately
              silent: it never gates submit (the backend enforces cardinality
              on submit), so surfacing a transient "checking" state would just
              flash a divider + message on every association edit. */}
          {(conflicts.length > 0 || conflictsError !== null) && (
            <div
              ref={conflictPanelRef}
              className='pt-3 border-t border-[var(--color-border-secondary)]'
              aria-describedby='conflict-panel-heading'
            >
              <ConflictResolutionPanel
                conflicts={conflicts}
                error={conflictsError}
                acknowledged={ackedEntityIds}
                onAcknowledge={handleAcknowledgeConflict}
                onRemoveTarget={handleRemoveConflictTarget}
              />
            </div>
          )}
        </div>
      </SlideoutMenu.Content>
      <SlideoutMenu.Footer>
        <CrmCreateFormFooter
          submitLabel={submitLabel}
          isSubmitting={isSubmitting}
          isReady={isReady}
          isCreatable={isCreatable}
          blockedByConflicts={blockedByConflicts}
          showCreateAnother={showCreateAnother}
          isDesktop={isDesktop}
          preferCreateAnother={preferCreateAnother}
          onToggleCreateDefault={toggleCreateDefault}
          onSubmit={handleSubmitClose}
          onSubmitAnother={handleSubmitAnother}
          onSubmitPreferred={handleSubmitPreferred}
          onCancel={handleClose}
          secondaryAction={renderFooterSecondaryAction?.(formData)}
          onSubmitClusterClickCapture={handleSubmitClusterClickCapture}
        />
      </SlideoutMenu.Footer>
    </div>
  );
}

// =============================================================================
// CrmCreateForm — outer wrapper that owns schema fetching, formData state, and
// FormProvider setup. CrmCreateFormContent (above) consumes FormContext.
// =============================================================================

export interface CrmCreateFormProps {
  config: CrmCreateConfig;
}

export function CrmCreateForm({ config }: CrmCreateFormProps) {
  const { orgId, entityType, validate } = config;
  const {
    schema: propertySchemas,
    loading: schemasLoading,
    error: schemasError,
  } = usePropertySchema(orgId, entityType);

  const [formData, setFormData] = useState<Record<string, unknown>>({});

  const fields = useMemo(() => allFields(config), [config]);

  // Config-level cross-field errors, recomputed per value change; each
  // field's validator reads its own entry so the form context surfaces them
  // through the same touched/visible machinery as schema errors.
  const configErrors = useMemo(
    () => validate?.(formData) ?? {},
    [validate, formData]
  );

  const fieldConfig = useMemo<Record<string, FieldConfig>>(() => {
    const propertyNames = fields.flatMap(f =>
      f.kind === 'property' ? [f.property] : []
    );
    const fromSchema = buildFieldConfigFromSchema(
      propertySchemas,
      propertyNames
    );
    const result: Record<string, FieldConfig> = {};
    for (const field of fields) {
      if (field.kind === 'association') {
        continue;
      }
      const base: FieldConfig =
        field.kind === 'property' ? (fromSchema[field.property] ?? {}) : {};
      const required = field.required ?? base.required;
      const schemaValidate = base.validate;
      result[field.property] = {
        required,
        validate: (value: unknown) =>
          schemaValidate?.(value) ?? configErrors[field.property],
      };
    }
    return result;
  }, [fields, propertySchemas, configErrors]);

  const fieldLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const field of fields) {
      if (field.kind === 'property') {
        const label = field.label ?? propertySchemas[field.property]?.label;
        if (label) {
          labels[field.property] = label;
        }
      } else if (field.kind === 'custom' && field.label) {
        labels[field.property] = field.label;
      }
    }
    return labels;
  }, [fields, propertySchemas]);

  return (
    <FormProvider
      fields={fieldConfig}
      values={formData}
      fieldLabels={fieldLabels}
    >
      <CrmCreateFormContent
        config={config}
        formData={formData}
        setFormData={setFormData}
        propertySchemas={propertySchemas}
        schemasLoading={schemasLoading}
        schemasError={schemasError}
      />
    </FormProvider>
  );
}
